import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { getCurrentUser } from 'aws-amplify/auth';
import Footer from '../components/Footer';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        await getCurrentUser();
        navigate('/upload');
      } catch (error) {
        // No existing session, stay on login page
      }
    };
    
    checkExistingSession();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await authService.signIn(username, password);
      navigate('/upload');
    } catch (err) {
      console.error('Error signing in:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <style>{`
        :root {
          --primary: #3a86ff;
          --secondary: #8338ec;
          --accent: #ff006e;
          --light: #f8f9fa;
          --dark: #212529;
          --success: #38b000;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
        }
        body {
          color: var(--dark);
          background-color: var(--light);
          line-height: 1.6;
        }
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        .btn {
          display: inline-block;
          padding: 12px 24px;
          background: var(--primary);
          color: white;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
          width: 100%;
        }
        .btn:hover {
          background: var(--secondary);
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
        }
        header {
          padding: 20px 0;
          background-color: white;
          box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
        }
        .navbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .logo {
          font-size: 1.8rem;
          font-weight: 800;
          color: var(--primary);
          text-decoration: none;
        }
        .logo span {
          color: var(--secondary);
        }
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(58, 134, 255, 0.1) 0%, rgba(131, 56, 236, 0.1) 100%);
          padding-top: 100px;
        }
        .login-card {
          background: white;
          padding: 40px;
          border-radius: 15px;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-width: 500px;
        }
        .form-group {
          margin-bottom: 20px;
        }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: var(--dark);
        }
        .form-group input {
          width: 100%;
          padding: 12px 15px;
          border: 1px solid #ced4da;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }
        .form-group input:focus {
          border-color: var(--primary);
          outline: none;
        }
        .error-alert {
          background: #f8d7da;
          color: #721c24;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          border: 1px solid #f5c6cb;
        }
        .back-link {
          color: var(--primary);
          text-decoration: none;
          font-weight: 500;
          margin-bottom: 20px;
          display: inline-block;
        }
        .back-link:hover {
          color: var(--secondary);
        }
      `}</style>
      
      <header>
        <div className="container">
          <nav className="navbar">
            <Link to="/" className="logo">Nexly<span>Digital</span></Link>
          </nav>
        </div>
      </header>

      <div className="login-container">
        <div className="login-card">
          <Link to="/" className="back-link">← Back to Home</Link>
          <h2 style={{marginBottom: '30px', color: 'var(--primary)', fontWeight: 'bold'}}>Sign In</h2>
          
          {error && (
            <div className="error-alert">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            
            <button type="submit" className="btn" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
          
          <p style={{marginTop: '20px', textAlign: 'center', color: '#6c757d', fontSize: '0.9rem'}}>
            Reach out to <a href="mailto:contact@nexlydigital.com" style={{color: 'var(--primary)', textDecoration: 'none'}}>contact@nexlydigital.com</a> for demo access.
          </p>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Login;